import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { CheckCircle2, FileText } from "lucide-react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useMe } from "../context/MeContext.js";
import { useToast } from "../context/ToastContext.js";
import { POLICY_STATUS_EVENT, type PolicyKind, type PolicySigningStatus, type SignedPolicy } from "../lib/policySigning.js";
import { PolicySignPanel, SignedPolicyDownloads } from "./PolicySignPanel.js";
import { Button } from "./ui/button.js";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog.js";

// The sign-off every logged-in employee meets before using the app: when a
// policy document is due for them — a version every employee must sign, its
// yearly re-sign from 1 January, or one HR asked them to sign — this pop-up
// opens on every authenticated page and cannot be closed until each is signed
// (rules: apps/api/src/lib/policySigning.ts). Signing goes through the same
// self-only endpoint as the onboarding wizard. The wizard itself lives
// outside AppShell, so it never shows this; its step 8 asks instead.
export function PolicySignGate() {
  const me = useMe();
  const toast = useToast();

  const [status, setStatus] = useState<PolicySigningStatus | null>(null);
  const [signedNow, setSignedNow] = useState<PolicyKind[]>([]);
  const [done, setDone] = useState(false);

  // Checked at login, on every page change, and straight after HR publishes a
  // document (POLICY_STATUS_EVENT) -- so whoever publishes a version every
  // employee signs, signs it too, there and then.
  const { pathname } = useLocation();
  const [recheck, setRecheck] = useState(0);
  useEffect(() => {
    const onChange = () => setRecheck(n => n + 1);
    window.addEventListener(POLICY_STATUS_EVENT, onChange);
    return () => window.removeEventListener(POLICY_STATUS_EVENT, onChange);
  }, []);

  // useMe() returns a blank-employeeId placeholder until /me/profile resolves.
  useEffect(() => {
    if (!me.employeeId) return;
    let cancelled = false;
    api.get<PolicySigningStatus>("/me/handbook-status")
      .then(({ data }) => {
        if (cancelled) return;
        setStatus(data);
        // Something new to sign reopens the pop-up, even after Continue.
        if (data.outstanding.length > 0) { setDone(false); setSignedNow([]); }
      })
      .catch((err) => { logError("PolicySignGate.load", err); });
    return () => { cancelled = true; };
  }, [me.employeeId, pathname, recheck]);

  // Mirrors the wizard: record when the handbook was actually opened.
  function markOpened(kind: PolicyKind) {
    if (kind !== "HANDBOOK") return;
    api.post(`/employees/${me.employeeId}/handbook/acknowledge`, { opened: true })
      .catch((err) => { logError("PolicySignGate.markOpened", err); });
  }

  async function sign(kind: PolicyKind) {
    try {
      const { data } = await api.post<PolicySigningStatus>(`/employees/${me.employeeId}/policies/${encodeURIComponent(kind)}/sign`, {});
      setStatus(data);
      setSignedNow(k => [...k, kind]);
      toast.show("Signed — thank you!", "success");
    } catch (err) {
      logError("PolicySignGate.sign", err, { kind });
      toast.show(errMsg(err) ?? "Failed to submit signature", "error");
      throw err;
    }
  }

  if (!me.employeeId || !status || done) return null;
  const pending = status.outstanding.length > 0;
  if (!pending && signedNow.length === 0) return null;

  // The copies signed in this sitting: the latest signature of each.
  const copies: SignedPolicy[] = signedNow
    .map(kind => status.signed.find(s => s.kind === kind))
    .filter((s): s is SignedPolicy => !!s);

  return (
    <Dialog open onOpenChange={() => { /* closes only through Continue, once everything is signed */ }}>
      <DialogContent
        hideClose
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        className="max-h-[90vh] w-[95vw] max-w-2xl"
      >
        {pending ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><FileText size={16} /> Please sign before you continue</DialogTitle>
              <DialogDescription>
                {status.outstanding.length === 1 ? "This document needs" : `These ${status.outstanding.length} documents need`} your signature.
                Open each one, read it to the end, agree and sign.
              </DialogDescription>
            </DialogHeader>
            <PolicySignPanel outstanding={status.outstanding} signerName={status.signerName} onOpened={markOpened} onSign={sign} />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[var(--color-success-fg)]"><CheckCircle2 size={16} /> All signed</DialogTitle>
              <DialogDescription>Thank you. Your signed copies are saved on your profile; download them here too.</DialogDescription>
            </DialogHeader>
            <SignedPolicyDownloads signed={copies} />
            <DialogFooter>
              <Button onClick={() => setDone(true)}>Continue</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
